import { Form } from 'antd';
import { notification } from 'components';
import { cleanObject } from 'utils/helpers';
import { useAppDispatch } from 'store/hooks';
import { useTypedSelector } from 'store/store';
import { ErrorMappingValueType } from 'typings';
import { useCallback, useMemo, useState } from 'react';
import { groupContactInfoByType } from 'utils/contactUtils';
import {
  resetContactInfoFields,
  resetEditId,
  resetIsChanged,
  resetOptionType,
} from 'components/common/ContactFieldManager/contactFieldManagerSlice';
import { useClientCreateMutation, useClientTypeQuery, useLanguageQuery } from 'api';
import { ContactInfoGrouped } from 'feature/clientTabs/general/contactInfo/useContactInfo';

export const useAddClient = (props: { closeModal: () => void }) => {
  const { closeModal } = props;
  const contacts = useTypedSelector((state) => state.contactFieldManager.contactInfoFields);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [createClient, { isLoading }] = useClientCreateMutation();
  const { data: clientTypeData = [] } = useClientTypeQuery();
  const { data: languageData = [] } = useLanguageQuery();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );
  const contactInfoToUpdate: ContactInfoGrouped = useMemo(() => groupContactInfoByType(contacts), [contacts]);
  const onValuesChange = useCallback(() => setDisableSaveBtn(false), []);

  const onCancel = useCallback(() => {
    form.resetFields();
    closeModal();
    setDisableSaveBtn(true);
    dispatch(resetEditId());
    dispatch(resetIsChanged());
    dispatch(resetOptionType());
    dispatch(resetContactInfoFields());
  }, [form, closeModal, dispatch]);

  const onClientAdd = useCallback(async () => {
    const { type, value, ...payload } = form.getFieldsValue() || {};
    let contactData = contactInfoToUpdate;
    try {
      await form.validateFields(['name', 'mainCountryId']);
    } catch {
      return;
    }
    if (!contacts.length) {
      contactData = type && value ? groupContactInfoByType([{ type, value }]) : ({} as ContactInfoGrouped);
    }
    const contactPerson = cleanObject({
      fullName: payload.fullName,
      languageId: payload.languageId,
      department: payload.department,
      role: payload.role,
      contacts: cleanObject(contactData),
    });
    payload.name = payload.name?.trim();
    payload.contactPerson = contactPerson;
    createClient(payload)
      .then(({ data, error }: any) => {
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: 'The client was successfully created.',
          });
          onCancel();
        } else if (!error.data.success) {
          const { data = {} } = error.data;
          Object.keys(data).forEach((_key: string) => {
            openNotificationWithIcon({
              type: 'error',
              message: data[_key],
            });
          });
        } else if (error.data.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      })
      .catch((err) => {
        console.log(err, 'error');
      });
  }, [createClient, openNotificationWithIcon, form, contactInfoToUpdate, onCancel, contacts]);

  return {
    form,
    contextHolder,
    isLoading,
    disableSaveBtn,
    languageData,
    clientTypeData,
    onClientAdd,
    onCancel,
    onValuesChange,
  };
};
